'use client';

import React, { useMemo, useState } from 'react';
import { faSignalBars } from '@fortawesome/pro-solid-svg-icons';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { useGetCrmStatusesSuggestionsQuery, useGetObjectDefinitionsListQuery, useGetFieldDefinitionsQuery, useGetMappingsQuery } from 'src/api/redux';
import {
  FilterIcon,
  IncludeExcludeSelect,
  ConnectorSelect,
  FilterAutocomplete,
  Divider,
  FilterLabel,
  ClearFilterButton,
} from 'src/components/FilterSets/components';
import { SelectedFilters } from 'src/components/FilterSets/SelectedFilters';
import type { FilterValue } from 'src/context/reducers/filterSetReducer';

const CRMStatuses = ({ location }: { location: string }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { filterSet, updateFilter, removeFilterValue } =
    useFilterSets(location);
  const filterValue = filterSet?.crmStatuses;

  // CRM statuses API returns all results (not paginated), filter locally
  const { data: allStatuses, isLoading, isFetching } =
    useGetCrmStatusesSuggestionsQuery({ q: '' });

  // Determine if the company uses MedRM by checking enabled mappings
  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  const isMedRM = useMemo(
    () =>
      allMappings?.results?.some(
        (mapping) => mapping.enabled && mapping.crm?.toLowerCase() === 'medrm'
      ) ?? false,
    [allMappings]
  );

  // For MedRM, fetch field definitions to map status values to labels
  const { data: objectDefinitions } = useGetObjectDefinitionsListQuery(
    undefined,
    { skip: !isMedRM }
  );
  const { data: fieldDefinitions } = useGetFieldDefinitionsQuery(
    { objectId: objectDefinitions?.results?.[0]?.id },
    { skip: !isMedRM || !objectDefinitions?.results?.[0]?.id }
  );

  // Create a lookup map from status value to label from MedRM field definitions
  const statusLabelMap = useMemo(() => {
    if (!isMedRM) return new Map<string, string>();
    const statusField = fieldDefinitions?.results?.find(
      (field) => field?.label?.toLowerCase() === 'status'
    );
    if (!statusField?.options) return new Map<string, string>();
    return new Map(
      statusField.options.map((opt) => [opt.value, opt.label])
    );
  }, [isMedRM, fieldDefinitions]);

  const options = useMemo(() => {
    if (!allStatuses) return [];

    const getStatusDisplayName = (crm_status: string) => {
      if (isMedRM && statusLabelMap.size > 0) {
        return statusLabelMap.get(crm_status) || crm_status;
      }
      return crm_status;
    };

    const getOptionLabel = (status: { crm_type: string; crm_status: string }) => {
      if (isMedRM && statusLabelMap.size > 0) {
        return getStatusDisplayName(status.crm_status);
      }
      return `${status.crm_type} - ${status.crm_status}`;
    };

    const filtered = searchTerm
      ? allStatuses.filter((status) =>
          getOptionLabel(status)
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      : allStatuses;
    return filtered.map((result) => ({
      // Store as JSON string of array [crm_type, crm_status]
      // This will be parsed back to actual array in Redux before sending to API
      value: JSON.stringify([result.crm_type, result.crm_status]),
      label: getOptionLabel(result),
    }));
  }, [allStatuses, searchTerm, isMedRM, statusLabelMap]);

  const handleRemoveFilter = (filterLabel: string) => {
    removeFilterValue('crmStatuses', filterLabel);
  };

  const handleFilterChange = (value: FilterValue) => {
    updateFilter('crmStatuses', value);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const hasValues = filterValue?.values?.length;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <FilterLabel>CRM Statuses</FilterLabel>
        <ClearFilterButton
          type="crmStatuses"
          isDisabled={!hasValues}
          location={location}
        />
      </div>
      <div className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-between items-center">
        <div className="flex-1 flex justify-start items-center gap-2">
          <FilterIcon icon={faSignalBars} />
          <IncludeExcludeSelect
            title="CRM Statuses"
            value={filterValue}
            onChange={handleFilterChange}
          />
          <Divider />
          <ConnectorSelect
            title="CRM Statuses"
            value={filterValue}
            onChange={handleFilterChange}
            isAll={filterValue?.is_all}
          />
          <Divider />
          <FilterAutocomplete
            options={options}
            value={filterValue}
            onChange={handleFilterChange}
            isLoading={isLoading || isFetching}
            placeholder="Search CRM statuses..."
            onSearch={handleSearch}
          />
        </div>
      </div>
      <SelectedFilters
        filters={filterValue?.values.map((v) => v.label)}
        onRemove={handleRemoveFilter}
        isAll={filterValue?.is_all}
      />
    </div>
  );
};

export default CRMStatuses;
